// @flow
import * as React from 'react';
import type { Location } from 'react-router';
import { Redirect, withRouter } from 'react-router';
import { connect } from 'react-redux';
import { isStaff as userIsStaff } from 'utils/currentUser';
import NotFoundPage from 'pages/NotFoundPage';
import LoadingPage from 'pages/LoadingPage';
import { hasParam } from 'utils/url';
import ContentPageContainer from 'containers/ContentPageContainer';
import { fetchPage as fetchPageRaw } from '../redux/main/actions/pages';
import type { PageStates, Page } from '../redux/main/reducers/pages';
import { setUserInitiatedSearch as setUserInitiatedSearchRaw } from '../redux/main/actions/mainSearch';

const { useEffect } = React;

const ContentPageContainerContainer = ({
  content,
  pageState,
  fetchPage,
  slug,
  isLandingPage,
  isStaff,
  amp,
  location
}: {
  content: Page,
  pageState: PageStates,
  fetchPage: Function,
  slug: string,
  isLandingPage: boolean,
  isStaff: boolean,
  amp: string,
  location: Location
}) => {
  const subsite =
    location.state && location.state.subsite
      ? location.state.subsite
      : content.subsite;
  const hasHomePage = content.subsite && !!content.subsite.homePage;
  const heroSearch = content.heroSearch;
  const defaultSearchType = heroSearch
    ? heroSearch.defaultSearchFilters.type
    : subsite
    ? subsite.defaultSearchType
    : 'JOB';
  const onlySearchResults = heroSearch && heroSearch.onlyResults;
  const onlyArticleResults = content.onlySearchResults && hasHomePage;
  // CMS pages might not have all the available translations
  const url =
    content.translations &&
    content.translations.reduce(
      (acc, cur) => {
        acc[cur.locale] = `/${cur.url}`;
        return acc;
      },
      { [content.locale]: content.url }
    );

  useEffect(
    () => {
      if (!amp && !isLandingPage) {
        fetchPage(slug);
      }
    },
    [fetchPage, slug, amp, isLandingPage]
  );

  if (hasParam(location.search, 'saved')) {
    return (
      <Redirect
        to={`${location.pathname}${
          location.search ? `${location.search}&` : '?'
        }searchMode=true`}
      />
    );
  }

  if (
    onlySearchResults ||
    isLandingPage ||
    (onlyArticleResults && hasHomePage)
  ) {
    return <Redirect to={`${location.pathname}?q=&searchMode=true`} />;
  }

  if (['FETCHING', 'UNFETCHED'].includes(pageState)) {
    if (amp) {
      // It's an AMP URL, but content is not already available.
      // Meaning it's disabled for that page.
      return <NotFoundPage />;
    }
    return <LoadingPage />;
  }

  if (content.id) {
    return (
      <ContentPageContainer
        content={content}
        isStaff={isStaff}
        defaultSearchType={defaultSearchType}
        url={url}
      />
    );
  }

  return <NotFoundPage />;
};

const mapStateToProps = (
  { currentUser, pages, mainSearch },
  { slug }: { slug: string }
) => {
  const content = pages.byUrl[slug] || {};
  return {
    content,
    isStaff: userIsStaff(currentUser),
    pageState: content.state || 'UNFETCHED',
    slug,
    isLandingPage: mainSearch ? !!mainSearch.isLandingPage : false
  };
};

export default withRouter(
  connect(
    mapStateToProps,
    {
      fetchPage: fetchPageRaw,
      setUserInitiatedSearch: setUserInitiatedSearchRaw
    }
  )(ContentPageContainerContainer)
);
